import { scorecardScoreColor } from '@/helpers/scorecard';

export type ScorecardItemType = 'percentage_slider' | 'scale_one_to_five' | 'scale_one_to_ten' | 'scale_one_to_fifteen' | 'scale_a_to_f' | 'pass_fail';

export interface ScorecardItemConfig {
    min: number;
    max: number;
    step: number;
    tickSize: number;
    tickLabels: (string | number)[];
    thumbSize: number;
}

// Default config values
const DEFAULT_CONFIG = {
    min: 0,
    step: 1,
    thumbSize: 32,
    tickSize: 6,
};

// Get configuration for each slider type
export function getScorecardItemConfig(type: ScorecardItemType): ScorecardItemConfig {
    switch (type) {
        case 'percentage_slider':
            return {
                ...DEFAULT_CONFIG,
                max: 100,
                tickSize: 1,
                tickLabels: Array.from({ length: 101 }, (_, i) => {
                    // Only show labels at key percentage points to avoid jumbled numbers
                    if (i === 0 || i === 25 || i === 50 || i === 75 || i === 100) {
                        return i;
                    }
                    return '';
                }),
            };
        
        case 'scale_one_to_five':
            return {
                ...DEFAULT_CONFIG,
                max: 5,
                tickLabels: ['N/a', 1, 2, 3, 4, 5],
            };
        
        case 'scale_one_to_ten':
            return {
                ...DEFAULT_CONFIG,
                max: 10,
                tickSize: 11,
                tickLabels: ['N/a', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            };
        
        case 'scale_one_to_fifteen':
            return {
                ...DEFAULT_CONFIG,
                max: 15,
                tickLabels: ['N/a', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
            };
        
        case 'scale_a_to_f':
            return {
                ...DEFAULT_CONFIG,
                max: 5,
                tickLabels: ['N/a', 'F', 'D', 'C', 'B', 'A'],
            };
        
        case 'pass_fail':
            return {
                ...DEFAULT_CONFIG,
                max: 2,
                tickLabels: ['N/a', 'Fail', 'Pass'],
            };
        
        default:
            throw new Error(`Unknown scorecard item type: ${type}`);
    }
}

// Get thumb label for display
export function getThumbLabel(type: ScorecardItemType, value: number): string {
    if (value === 0) {
        return 'N/a';
    }

    switch (type) {
        case 'percentage_slider':
            return value.toString();
        
        case 'scale_one_to_five':
        case 'scale_one_to_ten':
        case 'scale_one_to_fifteen':
            return value.toString();
        
        case 'scale_a_to_f':
            const gradeMap: { [key: number]: string } = {
                1: 'F',
                2: 'D',
                3: 'C',
                4: 'B',
                5: 'A',
            };
            return gradeMap[value] || value.toString();
        
        case 'pass_fail':
            if (value === 1) return 'Fail';
            if (value === 2) return 'Pass';
            return value.toString();
        
        default:
            return value.toString();
    }
}

// Get default slider value for a given type
export function getDefaultSliderValue(type: ScorecardItemType): number | null {
    switch (type) {
        case 'percentage_slider':
            return 100;
        case 'scale_one_to_five':
            return 5;
        case 'scale_one_to_ten':
            return 10;
        case 'scale_one_to_fifteen':
            return 15;
        case 'scale_a_to_f':
            return 5; // A
        case 'pass_fail':
            return 2; // Pass
        default:
            return null;
    }
}

// Convert answer string to number value for slider
export function convertAnswerToSliderValue(type: ScorecardItemType, answer: string | number | null, useDefaultIfNull: boolean = false): number | null {
    if (answer === null || answer === undefined || answer === '') {
        return useDefaultIfNull ? getDefaultSliderValue(type) : null;
    }

    const val = answer.toString();

    if (val === "0") {
        return null;
    }

    switch (type) {
        case 'percentage_slider':
            return parseInt(val);
        
        case 'scale_one_to_five':
        case 'scale_one_to_ten':
        case 'scale_one_to_fifteen':
            return parseInt(val);
        
        case 'scale_a_to_f':
            const gradeToNumber: { [key: string]: number } = {
                'f': 1,
                'd': 2,
                'c': 3,
                'b': 4,
                'a': 5,
            };
            const lowerVal = val.toLowerCase();
            return gradeToNumber[lowerVal] !== undefined ? gradeToNumber[lowerVal] : parseInt(val);
        
        case 'pass_fail':
            if (val === 'fail') return 1;
            if (val === 'pass') return 2;
            return parseInt(val);
        
        default:
            return parseInt(val);
    }
}

// Convert slider value to answer string
export function convertSliderValueToAnswer(type: ScorecardItemType, value: number | null): string | null {
    if (value === 0 || value === null) {
        return null;
    }

    switch (type) {
        case 'percentage_slider':
            return value.toString();
        
        case 'scale_one_to_five':
        case 'scale_one_to_ten':
        case 'scale_one_to_fifteen':
            return value.toString();
        
        case 'scale_a_to_f':
            const numberToGrade: { [key: number]: string } = {
                1: 'f',
                2: 'd',
                3: 'c',
                4: 'b',
                5: 'a',
            };
            return numberToGrade[value] || value.toString();
        
        case 'pass_fail':
            if (value === 1) return 'fail';
            if (value === 2) return 'pass';
            return value.toString();
        
        default:
            return value.toString();
    }
}

// Get color for slider based on value
export function getScorecardItemColor(type: ScorecardItemType, value: number | null): string {
    if (value === 0 || value === null) {
        return 'grey';
    }

    switch (type) {
        case 'percentage_slider':
            if (value < 60) return 'error';
            if (value < 80) return 'warning';
            return 'success';
        
        case 'scale_one_to_five':
            if (value <= 2) return 'error';
            if (value < 5) return 'warning';
            return 'success';
        
        case 'scale_one_to_ten':
            // Calculate points based on 1-10 scale
            let pointsScored10 = 0;
            switch (value) {
                case 1: pointsScored10 = 0; break;
                case 2: pointsScored10 = 11; break;
                case 3: pointsScored10 = 22; break;
                case 4: pointsScored10 = 33; break;
                case 5: pointsScored10 = 44; break;
                case 6: pointsScored10 = 56; break;
                case 7: pointsScored10 = 67; break;
                case 8: pointsScored10 = 78; break;
                case 9: pointsScored10 = 89; break;
                case 10: pointsScored10 = 100; break;
            }
            return scorecardScoreColor(pointsScored10, 100);
        
        case 'scale_one_to_fifteen':
            // Calculate points based on 1-15 scale
            let pointsScored15 = 0;
            switch (value) {
                case 1: pointsScored15 = 0; break;
                case 2: pointsScored15 = 7; break;
                case 3: pointsScored15 = 14; break;
                case 4: pointsScored15 = 21; break;
                case 5: pointsScored15 = 29; break;
                case 6: pointsScored15 = 36; break;
                case 7: pointsScored15 = 43; break;
                case 8: pointsScored15 = 50; break;
                case 9: pointsScored15 = 57; break;
                case 10: pointsScored15 = 64; break;
                case 11: pointsScored15 = 71; break;
                case 12: pointsScored15 = 79; break;
                case 13: pointsScored15 = 86; break;
                case 14: pointsScored15 = 93; break;
                case 15: pointsScored15 = 100; break;
            }
            return scorecardScoreColor(pointsScored15, 100);
        
        case 'scale_a_to_f':
            // Calculate points based on letter grade
            let pointsScored = 0;
            switch (value) {
                case 1: pointsScored = 20; break; // F
                case 2: pointsScored = 40; break; // D
                case 3: pointsScored = 60; break; // C
                case 4: pointsScored = 80; break; // B
                case 5: pointsScored = 100; break; // A
            }
            return scorecardScoreColor(pointsScored, 100);
        
        case 'pass_fail':
            if (value === 1) return 'error';
            return 'success';
        
        default:
            return 'grey';
    }
}

